import { type ReactElement } from 'react';
import { Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSecurityGroups } from '@/hooks/api/useSecurityGroups';

/**
 * Sentinel `Select` value for the organization scope. The Radix Select cannot
 * hold an empty-string value, so `null` (organization) needs a stand-in that no
 * group id can collide with.
 *
 * Intentionally NOT the alerts contract's `ALERT_ORGANIZATION_SCOPE_FILTER_VALUE`,
 * despite the identical literal. That one is a wire value the alerts API parses;
 * this one never leaves this component. Sharing them would couple an internal
 * widget detail to a public contract, so a change to the alerts filter encoding
 * would silently alter this dropdown. They are never compared to each other, so
 * "drift" between them has no consequence.
 */
const ORGANIZATION_SCOPE_VALUE = '__organization__';

const ORGANIZATION_SCOPE_LABEL = 'Organization (default)';

/**
 * Scope selector for the Policies page (KIR-3800, AC #6 and #7).
 *
 * Renders as an "Applies to" bar above the policy list, plus an explanatory
 * notice when a group scope is active. The notice is not decoration: the page
 * below looks identical in both scopes, so without it a group edit is
 * indistinguishable from an organization-wide one.
 */
export function PolicyScopeSelector({
  organizationId,
  selectedGroupId,
  onScopeChange,
  disabled = false,
}: {
  organizationId: string;
  /** `null` selects the organization scope. */
  selectedGroupId: string | null;
  /** Called with the new group id, or `null` for the organization scope. */
  onScopeChange: (groupId: string | null) => void;
  disabled?: boolean;
}): ReactElement | null {
  const { data: groups, isLoading } = useSecurityGroups(organizationId);

  // With no groups there is only one scope to pick, so the selector would be a
  // dead control — hide it and leave the page exactly as it was before.
  if (isLoading || !groups || groups.length === 0) return null;

  const selectedGroup = groups.find(group => group.id === selectedGroupId);

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold">Applies to</span>
            <Select
              value={selectedGroupId ?? ORGANIZATION_SCOPE_VALUE}
              disabled={disabled}
              onValueChange={value =>
                onScopeChange(value === ORGANIZATION_SCOPE_VALUE ? null : value)
              }
            >
              <SelectTrigger className="w-[280px]" aria-label="Applies to">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ORGANIZATION_SCOPE_VALUE}>
                  {ORGANIZATION_SCOPE_LABEL}
                </SelectItem>
                {groups.map(group => (
                  <SelectItem key={group.id} value={group.id}>
                    Security Group: {group.name} ({group.userCount ?? 0}{' '}
                    {group.userCount === 1 ? 'user' : 'users'})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <span className="text-sm text-muted-foreground">
            Changes are saved for the selected scope
          </span>
        </CardContent>
      </Card>

      {selectedGroup ? (
        <div
          role="note"
          className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-100"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            You are editing policies for the{' '}
            <span className="font-semibold">{selectedGroup.name}</span> group.
            These settings override the organization defaults for members of
            this group. Users who are not in this group keep the organization
            defaults.
          </p>
        </div>
      ) : null}
    </div>
  );
}
